import React from "react";
import { PencilLine, ShieldCheck } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DISCLAIMERS } from "@/lib/compliance";
import { CONTACT_TOPICS } from "@/lib/services";

const HELP_OPTIONS = CONTACT_TOPICS;

/**
 * Help-topic select + free-text "tell us more" textarea. Includes the
 * sensitive-info warning that has to appear directly above the textarea
 * so visitors see it before typing free-form content.
 */
export default function InquiryHelpFields({ values, errors, update }) {
    return (
        <>
            <div className="mt-4">
                <label
                    htmlFor="help_topic"
                    className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft"
                >
                    What do you need help with?
                </label>
                <Select
                    value={values.help_topic}
                    onValueChange={(v) => update("help_topic", v)}
                >
                    <SelectTrigger
                        id="help_topic"
                        data-testid="form-help-select"
                        aria-required="true"
                        aria-invalid={!!errors.help_topic}
                        aria-describedby={
                            errors.help_topic ? "help_topic-error" : undefined
                        }
                        className="h-[52px] w-full border-[rgba(212,175,55,0.25)] bg-[#0b0e13] text-left text-brand-ivory hover:border-[rgba(212,175,55,0.45)] focus:border-brand-gold focus:ring-2 focus:ring-[rgba(212,175,55,0.15)] focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                    >
                        <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="border-[rgba(212,175,55,0.35)] bg-[#0b0e13] text-brand-ivory">
                        {HELP_OPTIONS.map((opt) => (
                            <SelectItem
                                key={opt}
                                value={opt}
                                data-testid={`form-help-option-${opt
                                    .toLowerCase()
                                    .replace(/[\s/]+/g, "-")}`}
                                className="focus:bg-[rgba(212,175,55,0.12)] focus:text-brand-gold2"
                            >
                                {opt}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.help_topic && (
                    <p
                        id="help_topic-error"
                        role="alert"
                        className="mt-1 text-xs text-red-400"
                    >
                        {errors.help_topic}
                    </p>
                )}
            </div>

            <div className="mt-4">
                <label
                    htmlFor="help_note"
                    className="mb-2 block text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft"
                >
                    Tell us more (optional)
                </label>
                <p
                    data-testid="form-sensitive-warning-pre-note"
                    className="mb-2 flex items-start gap-2 rounded-md border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.04)] px-3 py-2 text-[11px] leading-relaxed text-brand-ivory/85"
                >
                    <ShieldCheck
                        aria-hidden="true"
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-gold2"
                    />
                    <span>{DISCLAIMERS.sensitiveInfoWarning}</span>
                </p>
                <div className="relative">
                    <PencilLine
                        aria-hidden="true"
                        className="pointer-events-none absolute left-3.5 top-4 h-4 w-4 text-brand-goldSoft"
                    />
                    <textarea
                        id="help_note"
                        data-testid="form-note-textarea"
                        data-ph-mask="true"
                        className="enc-input with-icon ph-no-capture min-h-[96px] resize-none"
                        placeholder="Brief note (optional)"
                        value={values.help_note}
                        onChange={(e) => update("help_note", e.target.value)}
                    />
                </div>
            </div>
        </>
    );
}
